/**
 * 视频性能优化工具函数
 * 提供视频加载、缓存、错误处理等优化功能
 */

// 视频加载状态枚举
export const VideoLoadState = {
  IDLE: 'idle',
  LOADING: 'loading',
  LOADED: 'loaded',
  ERROR: 'error',
  PLAYING: 'playing',
  PAUSED: 'paused'
};

// 设备类型检测
export const DeviceType = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop'
};

/**
 * 检测设备类型
 */
export function detectDeviceType() {
  if (typeof window === 'undefined') return DeviceType.DESKTOP;
  
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /mobile|android|iphone|ipad|phone/i.test(userAgent);
  const isTablet = /tablet|ipad/i.test(userAgent);
  
  if (isMobile && !isTablet) return DeviceType.MOBILE;
  if (isTablet) return DeviceType.TABLET;
  return DeviceType.DESKTOP;
}

/**
 * 检测网络连接质量
 */
export function detectNetworkQuality() {
  if (typeof navigator === 'undefined' || !navigator.connection) {
    return 'unknown';
  }
  
  const connection = navigator.connection;
  const effectiveType = connection.effectiveType || 'unknown';
  const downlink = connection.downlink || 0;
  
  if (effectiveType === '4g' && downlink > 10) return 'excellent';
  if (effectiveType === '4g' && downlink > 5) return 'good';
  if (effectiveType === '3g' || downlink > 1.5) return 'fair';
  return 'poor';
}

/**
 * 根据设备和网络状况优化视频配置
 */
export function getOptimizedVideoConfig(videoUrl, deviceType = null, networkQuality = null) {
  const device = deviceType || detectDeviceType();
  const network = networkQuality || detectNetworkQuality();
  
  const config = {
    preload: 'metadata',
    autoplay: false,
    muted: true,
    playsInline: true,
    controls: false,
    loop: true,
    poster: null,
    quality: 'auto'
  };
  
  // 根据设备类型调整配置
  switch (device) {
    case DeviceType.MOBILE:
      config.preload = 'metadata';
      config.quality = network === 'excellent' ? 'high' : 'medium';
      break;
    case DeviceType.TABLET:
      config.preload = 'metadata';
      config.quality = network === 'poor' ? 'low' : 'medium';
      break;
    case DeviceType.DESKTOP:
      config.preload = network === 'excellent' ? 'auto' : 'metadata';
      config.quality = network === 'poor' ? 'medium' : 'high';
      break;
  }
  
  // 根据网络质量调整配置
  if (network === 'poor') {
    config.preload = 'none';
    config.quality = 'low';
  }
  
  return config;
}

/**
 * 创建视频元素并应用优化配置
 */
export function createOptimizedVideoElement(videoUrl, config = {}) {
  const video = document.createElement('video');
  
  // 应用基础配置
  Object.assign(video, {
    preload: config.preload || 'metadata',
    autoplay: config.autoplay || false,
    muted: config.muted || true,
    playsInline: config.playsInline || true,
    controls: config.controls || false,
    loop: config.loop || true
  });
  
  // 添加性能优化属性
  video.style.transform = 'translateZ(0)';
  video.style.backfaceVisibility = 'hidden';
  video.style.webkitBackfaceVisibility = 'hidden';
  
  // 添加源
  const source = document.createElement('source');
  source.src = videoUrl;
  source.type = 'video/mp4';
  video.appendChild(source);
  
  return video;
}

/**
 * 视频加载性能监控
 */
export class VideoPerformanceMonitor {
  constructor() {
    this.metrics = {
      loadTime: 0,
      bufferingTime: 0,
      errorCount: 0,
      playCount: 0,
      pauseCount: 0
    };
    this.startTime = 0;
  }
  
  startMonitoring(videoElement) {
    this.startTime = performance.now();
    
    videoElement.addEventListener('loadstart', this.handleLoadStart.bind(this));
    videoElement.addEventListener('canplay', this.handleCanPlay.bind(this));
    videoElement.addEventListener('waiting', this.handleWaiting.bind(this));
    videoElement.addEventListener('playing', this.handlePlaying.bind(this));
    videoElement.addEventListener('pause', this.handlePause.bind(this));
    videoElement.addEventListener('error', this.handleError.bind(this));
  }
  
  handleLoadStart() {
    this.startTime = performance.now();
  }
  
  handleCanPlay() {
    this.metrics.loadTime = performance.now() - this.startTime;
    console.log(`🎬 Video loaded in ${this.metrics.loadTime.toFixed(2)}ms`);
  }
  
  handleWaiting() {
    this.metrics.bufferingTime += 100; // 简化计算
  }
  
  handlePlaying() {
    this.metrics.playCount++;
  }
  
  handlePause() {
    this.metrics.pauseCount++;
  }
  
  handleError(event) {
    this.metrics.errorCount++;
    console.error('🎬 Video error:', event);
  }
  
  getMetrics() {
    return { ...this.metrics };
  }
}

/**
 * 视频错误处理和重试机制
 */
export class VideoErrorHandler {
  constructor(maxRetries = 3) {
    this.maxRetries = maxRetries;
    this.retryCount = 0;
    this.retryDelay = 1000;
  }
  
  async handleError(videoElement, videoUrl, onRetry, onFail) {
    this.retryCount++;
    
    if (this.retryCount <= this.maxRetries) {
      console.log(`🎬 Retrying video load (${this.retryCount}/${this.maxRetries})`);
      
      // 指数退避
      const delay = this.retryDelay * Math.pow(2, this.retryCount - 1);
      
      setTimeout(() => {
        videoElement.src = videoUrl;
        videoElement.load();
        onRetry && onRetry(this.retryCount);
      }, delay);
    } else {
      console.error('🎬 Video failed to load after all retries');
      onFail && onFail();
    }
  }
  
  reset() {
    this.retryCount = 0;
  }
}

/**
 * 视频缓存管理
 */
export class VideoCacheManager {
  constructor() {
    this.cache = new Map();
    this.maxCacheSize = 10;
  }
  
  set(key, videoElement) {
    if (this.cache.size >= this.maxCacheSize) {
      const firstKey = this.cache.keys().next().value;
      this.cache.delete(firstKey);
    }
    
    this.cache.set(key, {
      element: videoElement,
      timestamp: Date.now(),
      accessCount: 0
    });
  }
  
  get(key) {
    const cached = this.cache.get(key);
    if (cached) {
      cached.accessCount++;
      cached.timestamp = Date.now();
      return cached.element;
    }
    return null;
  }
  
  clear() {
    this.cache.clear();
  }
  
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      entries: Array.from(this.cache.entries()).map(([key, value]) => ({
        key,
        accessCount: value.accessCount,
        age: Date.now() - value.timestamp
      }))
    };
  }
}

/**
 * 视频预加载管理器
 */
export class VideoPreloadManager {
  constructor() {
    this.preloadQueue = [];
    this.isProcessing = false;
  }
  
  addToQueue(videoUrl, priority = 'normal') {
    this.preloadQueue.push({ url: videoUrl, priority });
    this.preloadQueue.sort((a, b) => {
      const priorityOrder = { high: 3, normal: 2, low: 1 };
      return priorityOrder[b.priority] - priorityOrder[a.priority];
    });
    
    if (!this.isProcessing) {
      this.processQueue();
    }
  }
  
  async processQueue() {
    if (this.isProcessing || this.preloadQueue.length === 0) return;
    
    this.isProcessing = true;
    
    while (this.preloadQueue.length > 0) {
      const { url } = this.preloadQueue.shift();
      
      try {
        await this.preloadVideo(url);
      } catch (error) {
        console.warn('🎬 Failed to preload video:', url, error);
      }
      
      // 添加延迟避免阻塞主线程
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.isProcessing = false;
  }
  
  async preloadVideo(url) {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.style.display = 'none';
      
      video.addEventListener('loadedmetadata', () => {
        document.body.removeChild(video);
        resolve();
      });
      
      video.addEventListener('error', () => {
        document.body.removeChild(video);
        reject(new Error('Failed to preload video'));
      });
      
      video.src = url;
      document.body.appendChild(video);
    });
  }
}

// 导出单例实例
export const videoCacheManager = new VideoCacheManager();
export const videoPreloadManager = new VideoPreloadManager(); 